import { useFetchInstance } from '@/composables/api/useFetchInstance';

export interface SaveDesignScreenshotInput {
    label: string;
    dataUrl: string;
}

function dataUrlToFile(dataUrl: string, filename: string): File {
    const comma = dataUrl.indexOf(',');
    if (comma === -1) {
        throw new Error('Invalid data URL');
    }
    const header = dataUrl.slice(0, comma);
    const base64 = dataUrl.slice(comma + 1);
    const mimeMatch = header.match(/data:(.*?);base64/);
    const mime = mimeMatch?.[1] || 'image/jpeg';
    const binary = atob(base64);
    const bytes = new Uint8Array(binary.length);
    for (let i = 0; i < binary.length; i++) {
        bytes[i] = binary.charCodeAt(i);
    }
    return new File([bytes], filename, { type: mime });
}

/**
 * Corps multipart attendu par POST /api/save_designs/create :
 * name, productId, ecommerceCustomerId (optionnel), saveJson (fichier), screenshots[] (fichiers).
 */
export function buildSaveDesignFormData(params: {
    name: string;
    productId: string;
    ecommerceCustomerId?: string | null;
    qtySelected?: string | number | null;
    /** Méthode d'impression courante (digital/screen/embroidery) — filtre des sauvegardes. */
    printMethod?: string | null;
    saveJson: string;
    screenshots: SaveDesignScreenshotInput[];
    /** Mode export : email de reception du lien de reprise. */
    email?: string | null;
    /** Langue de navigation (email localise). */
    lang?: string | null;
}): FormData {
    const formData = new FormData();
    formData.append('name', params.name);
    formData.append('productId', params.productId);
    if (params.ecommerceCustomerId) {
        formData.append('ecommerceCustomerId', params.ecommerceCustomerId);
    }
    // Mode export : le backend envoie un email de reprise (Resend) si `email` fourni.
    if (params.email) {
        formData.append('email', params.email);
        formData.append('lang', params.lang || 'en');
    }
    if (params.qtySelected !== undefined && params.qtySelected !== null && params.qtySelected !== '') {
        formData.append('qtySelected', String(params.qtySelected));
    }
    if (params.printMethod) {
        formData.append('printMethod', params.printMethod);
    }
    const idPrefix = params.productId.replace(/-/g, '').slice(0, 12) || 'design';
    const saveBlob = new Blob([params.saveJson], { type: 'application/json' });
    const saveFile = new File([saveBlob], `${idPrefix}_save.json`, { type: 'application/json' });
    formData.append('saveJson', saveFile);
    params.screenshots.forEach((s, i) => {
        const safe = (s.label || `screenshot_${i}`).replace(/[^a-z0-9_-]/gi, '_').toLowerCase() || `screenshot_${i}`;
        formData.append('screenshots[]', dataUrlToFile(s.dataUrl, `${safe}.jpg`));
    });
    return formData;
}

export function extractRecordIdFromSaveDesignResponse(data: unknown): string | undefined {
    if (data == null || typeof data !== 'object') {
        return undefined;
    }
    const o = data as Record<string, unknown>;
    const direct = o.recordId ?? o.record_id ?? o.RecordID ?? o.id;
    if (typeof direct === 'string' && direct) {
        return direct;
    }
    for (const key of ['record', 'save', 'data'] as const) {
        const nested = o[key];
        if (nested && typeof nested === 'object') {
            const n = nested as Record<string, unknown>;
            const id = n.id ?? n.recordId ?? n.record_id;
            if (typeof id === 'string' && id) {
                return id;
            }
        }
    }
    return undefined;
}

export interface SaveDesignScreen {
    id: string;
    path: string;
}

export interface SaveDesignMember {
    id: string;
    name: string;
    product: string;
    site: string;
    createdAt: string;
    jsonSave: string;
    ecommerceCustomerId?: string;
    printMethod?: string | null;
    screens: SaveDesignScreen[];
}

export interface SaveDesignsResponse {
    totalItems: number;
    member: SaveDesignMember[];
}

export function useSaveDesigns() {
    async function createSaveDesign(formData: FormData) {
        return useFetchInstance('/api/save_designs/create').post(formData).json();
    }

    async function getSaveDesigns(params: {
        ecommerceCustomerId?: string;
        productId?: string;
        siteId?: string;
        page?: number;
        itemsPerPage?: number;
    }) {
        const query = new URLSearchParams();
        if (params.ecommerceCustomerId) {
            query.set('ecommerceCustomerId', params.ecommerceCustomerId);
        }
        if (params.productId) {
            query.set('product.id', params.productId);
        }
        if (params.siteId) {
            query.set('site.id', params.siteId);
        }
        if (params.page) {
            query.set('page', String(params.page));
        }
        if (params.itemsPerPage) {
            query.set('itemsPerPage', String(params.itemsPerPage));
        }

        const qs = query.toString();
        const url = `/api/save_designs${qs ? `?${qs}` : ''}`;
        return useFetchInstance(url).get().json<SaveDesignsResponse>();
    }

    async function fetchJsonSaveFromUrl(jsonSaveUrl: string): Promise<Record<string, unknown> | null> {
        try {
            const response = await fetch(jsonSaveUrl);
            if (!response.ok) {
                return null;
            }
            return await response.json();
        } catch (e) {
            console.error('[fetchJsonSaveFromUrl] Failed to fetch jsonSave:', e);
            return null;
        }
    }

    async function deleteSaveDesign(id: string) {
        return useFetchInstance(`/api/save_designs/${id}`).delete();
    }

    return { createSaveDesign, getSaveDesigns, fetchJsonSaveFromUrl, deleteSaveDesign };
}
